var __jsx = React.createElement;
import React from "react";
import { faNoteSticky, faPlus } from "@fortawesome/pro-regular-svg-icons";
import AwesomeIcon from "../AwesomeIcon";
import { getDaysUntilDue } from "../../utils";
import classNames from "classnames";
import Link from "next/link";
import Store from "../../utils/Store";
import { VIEWS } from "../SummarySection";
import Tooltip from "../common/Tooltip";
import { useTranslation } from "react-i18next";
/**
 * Determines which color indicators should be shown based on all reminders
 * Returns both boolean flags and counts for each category
 */
var getReminderIndicators = function getReminderIndicators(reminders) {
  if (!reminders || reminders.length === 0) {
    return {
      overdueCount: 0,
      urgentCount: 0,
      upcomingCount: 0,
      doneCount: 0,
      hasRed: false,
      hasYellow: false,
      hasGrey: false
    };
  }
  var overdueCount = 0;
  var urgentCount = 0;
  var upcomingCount = 0;
  var doneCount = 0;
  reminders.forEach(function (reminder) {
    if (reminder.resolved) {
      doneCount++;
      return;
    }
    var _getDaysUntilDue = getDaysUntilDue(reminder.remind_after),
      daysRemaining = _getDaysUntilDue.daysRemaining;

    // Red: overdue
    if (daysRemaining < 0) {
      overdueCount++;
    }
    // Yellow: due in 7 or less days (urgent)
    else if (daysRemaining <= 7) {
      urgentCount++;
    }
    // Grey: due in more than 7 days (upcoming)
    else {
      upcomingCount++;
    }
  });
  return {
    overdueCount: overdueCount,
    urgentCount: urgentCount,
    upcomingCount: upcomingCount,
    doneCount: doneCount,
    hasRed: overdueCount > 0,
    hasYellow: urgentCount > 0,
    hasGrey: upcomingCount > 0
  };
};

/**
 * Displays a pill-shaped badge with a note icon and reminder count.
 * Shows color-coded indicators on the top-right of the icon.
 *
 * Clicking the badge navigates to the patient page:
 * - If count is 0 (plus icon): Opens the CREATE_EDIT_REMINDER view
 * - If count > 0: Opens the REMINDERS list view
 *
 * Hovering over badges with counts shows a tooltip with breakdown:
 * - Overdue count (reminders past due date)
 * - Urgent count (reminders due within 7 days)
 * - Upcoming count (reminders due in more than 7 days)
 * - Done count (resolved reminders)
 *
 * Color Coding (multiple dots can appear):
 * - Red dot: At least one overdue reminder
 * - Yellow/Amber dot: At least one urgent reminder
 * - Grey dot: At least one upcoming reminder
 */

var ReminderCountBadge = function ReminderCountBadge(_ref) {
  var _reminders$filter;
  var reminders = _ref.reminders,
    reminderDateFormatted = _ref.reminderDateFormatted,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? "" : _ref$className,
    patientUid = _ref.patientUid,
    _ref$tooltipPosition = _ref.tooltipPosition,
    tooltipPosition = _ref$tooltipPosition === void 0 ? "left" : _ref$tooltipPosition,
    _ref$patientRouteBase = _ref.patientRouteBase,
    patientRouteBase = _ref$patientRouteBase === void 0 ? "/patients" : _ref$patientRouteBase;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var count = (reminders === null || reminders === void 0 || (_reminders$filter = reminders.filter(function (reminder) {
    return !reminder.resolved;
  })) === null || _reminders$filter === void 0 ? void 0 : _reminders$filter.length) || 0;
  var showAddIcon = count === 0;
  var _getReminderIndicator = getReminderIndicators(reminders),
    hasRed = _getReminderIndicator.hasRed,
    hasYellow = _getReminderIndicator.hasYellow,
    hasGrey = _getReminderIndicator.hasGrey,
    overdueCount = _getReminderIndicator.overdueCount,
    urgentCount = _getReminderIndicator.urgentCount,
    upcomingCount = _getReminderIndicator.upcomingCount,
    doneCount = _getReminderIndicator.doneCount;
  var hasIndicators = hasRed || hasYellow || hasGrey;

  // Create tooltip content showing breakdown
  var tooltipContent = /*#__PURE__*/React.createElement("div", {
    className: "p-3 text-md font-medium grid grid-cols-[2fr_1fr] gap-x-1 gap-y-2 text-gray-900 dark:text-white"
  }, /*#__PURE__*/React.createElement("span", null, t("overdue")), /*#__PURE__*/React.createElement("span", {
    className: "dark:text-gray-400 text-gray-600 text-right"
  }, overdueCount), /*#__PURE__*/React.createElement("span", null, t("urgent")), /*#__PURE__*/React.createElement("span", {
    className: "dark:text-gray-400 text-gray-600 text-right"
  }, urgentCount), /*#__PURE__*/React.createElement("span", null, t("upcoming")), /*#__PURE__*/React.createElement("span", {
    className: "dark:text-gray-400 text-gray-600 text-right"
  }, upcomingCount), /*#__PURE__*/React.createElement("span", null, t("done")), /*#__PURE__*/React.createElement("span", {
    className: "dark:text-gray-400 text-gray-600 text-right"
  }, doneCount));
  var handleClick = function handleClick(view) {
    return function (e) {
      e.stopPropagation();
      var uid = patientUid === null || patientUid === void 0 ? void 0 : patientUid.toString();
      Store.update(function (s) {
        s.initialSummaryView = view;
        s.isSummaryCollapsed = false;
        s.scrollToPatientUid = uid;
      });
    };
  };
  var badgeContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faNoteSticky,
    className: classNames("text-gray-600 dark:text-gray-400 w-5 h-5", {
      "opacity-40": !hasIndicators
    })
  }), hasIndicators && /*#__PURE__*/React.createElement("div", {
    className: "absolute top-1 left-[90%] -translate-x-1/2 flex bg-gray-200 dark:bg-gray-800 rounded-full p-1"
  }, hasRed && /*#__PURE__*/React.createElement("span", {
    className: "w-1.5 h-1.5 rounded-full bg-red-500"
  }), hasYellow && /*#__PURE__*/React.createElement("span", {
    className: "w-1.5 h-1.5 rounded-full bg-amber-500"
  }), hasGrey && /*#__PURE__*/React.createElement("span", {
    className: "w-1.5 h-1.5 rounded-full bg-gray-400"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "text-base text-gray-700 dark:text-gray-400"
  }, showAddIcon ? /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faPlus,
    size: "sm"
  }) : count));
  var badgeClasses = "inline-flex items-end ".concat(hasIndicators ? "gap-3" : "gap-2", " px-4 py-1 rounded-2xl bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700 transition-colors cursor-pointer ").concat(className);
  var badgeLink = /*#__PURE__*/React.createElement(Link, {
    href: "".concat(patientRouteBase, "/").concat(patientUid),
    onClick: handleClick(showAddIcon ? VIEWS.CREATE_EDIT_REMINDER : VIEWS.REMINDERS),
    className: badgeClasses,
    title: showAddIcon ? t("label.add_task") : undefined
  }, badgeContent);

  // Show tooltip only when there are reminders
  if (showAddIcon) {
    return badgeLink;
  }
  return /*#__PURE__*/React.createElement(Tooltip, {
    tooltipText: tooltipContent,
    position: tooltipPosition,
    verticalPosition: "bottom",
    className: "[&_.tooltip-trigger]:m-0 [&_.tooltip-content]:rounded-md [&_.tooltip-content]:bg-gray-200 dark:[&_.tooltip-content]:bg-gray-800 [&_.tooltip-arrow]:border-b-gray-200 dark:[&_.tooltip-arrow]:border-b-gray-800"
  }, badgeLink);
};
export default ReminderCountBadge;